import React from 'react';
import { Field } from '@api';
import { Modal } from './seachpage-modal';
import { getInputType, isAmountField, isDateField } from './seachpage-helpers';
import './EditDocumentModal.css';

export interface EditDocumentModalProps {
  doc: any;
  documentFields: Field[];
  values: Record<string, any>;
  onChange: (values: Record<string, any>) => void;
  onCancel: () => void;
  onSave: () => void;
  saving?: boolean;
  error?: string | null;
}

export const EditDocumentModal: React.FC<EditDocumentModalProps> = ({
  doc,
  documentFields,
  values,
  onChange,
  onCancel,
  onSave,
  saving,
  error
}) => {
  // Helper function to format currency values for display
  const formatCurrencyValue = (field: Field, value: any): string => {
    if (!isAmountField(field)) return value || '';
    if (value === '' || value == null) return '';
    const num = typeof value === 'number' ? value : parseFloat(value);
    return isNaN(num) ? (value || '') : num.toFixed(2);
  };
  
  // Helper function to handle currency input changes
  const handleCurrencyChange = (field: Field, inputValue: string) => {
    if (!isAmountField(field)) return inputValue;
    // Allow empty string or valid decimal numbers
    if (inputValue === '') return '';
    const num = parseFloat(inputValue);
    return isNaN(num) ? inputValue : inputValue; // Keep the raw input for editing
  };

  // Build ordered fields using the provided array (assumed to be in endpoint order)
  // and insert a pseudo-field 'subfolder' after the last folder field
  const orderedFields: Field[] = React.useMemo(() => {
    const arr = Array.isArray(documentFields) ? documentFields.slice() : [];
    // find last folder field index
    let lastFolderIdx = -1;
    for (let i = 0; i < arr.length; i++) {
      if (arr[i]?.folderfield) lastFolderIdx = i;
    }
    const out: Field[] = [];
    const pseudoSub = { name: 'subfolder', title: 'Subfolder', fieldType: 'text', folderfield: false } as unknown as Field;
    
      // no folder fields, insert subfolder at start
      out.push(pseudoSub);
      out.push(...arr);
    return out;
  }, [documentFields]);

  return (
    // Increased width and maxHeight
    <Modal title={`Edit Document #${doc?.id ?? ''}`} onClose={onCancel} width={700} showClose={false}>
      {error && <div className="edit-document-modal__error">{error}</div>}
      <div className="edit-document-modal__container">
        <div className="edit-document-modal__description-section">
          <span className="edit-document-modal__field-label">Description</span>
          <input 
            value={values.description||''} 
            onChange={e=> onChange({ ...values, description: e.target.value })} 
            className="edit-document-modal__input" 
          />
        </div>
        <div className="edit-document-modal__fields-container">
          <div className="edit-document-modal__fields-grid">
            {orderedFields.map(field=> {
              const k = field.name;
              const inputType = getInputType(field as Field);
              const isCheckbox = inputType === 'checkbox';
              const isCurrency = isAmountField(field as Field);
              const isDate = isDateField(field as Field);
              const raw = values[k];
              const rawStr = typeof raw === 'string' ? raw : (raw==null ? '' : String(raw));

              if (k === 'subfolder') {
                // use doc.folder.folderdescription as requested, fallback to values.subfolder
                const subVal = (doc.folder.folderDescription ?? '');
                return (
                  <React.Fragment key={k}>
                    <span className="edit-document-modal__field-label">Subfolder</span>
                    <input type="text" value={subVal} readOnly className="edit-document-modal__input" />
                  </React.Fragment>
                );
              }

              let valueForInput: string;
              if (inputType === 'date') {
                valueForInput = rawStr?.split('T')[0] ?? '';
              } else if (isCurrency) {
                valueForInput = formatCurrencyValue(field as Field, raw);
              } else {
                valueForInput = rawStr;
              }
               
              // Choose appropriate CSS class
              const inputClass = isDate ? 'edit-document-modal__input--date' : 'edit-document-modal__input';
               
              return (
                <React.Fragment key={k}>
                  <span className="edit-document-modal__field-label">{(field as Field).title ?? k}</span>
                  {isCheckbox ? (
                    <input
                      type="checkbox"
                      checked={rawStr.toLowerCase() === 'true'}
                      onChange={e=> onChange({ ...values, [k]: e.target.checked ? 'true' : 'false' })}
                    />
                  ) : (
                    <input
                      type={isCurrency ? 'number' : inputType}
                      step={isCurrency ? '0.01' : undefined}
                      value={valueForInput ?? ''}
                      onChange={e=> {
                        const inputValue = (e.target as HTMLInputElement).value;
                        const processedValue = isCurrency ? handleCurrencyChange(field as Field, inputValue) : inputValue;
                        onChange({ ...values, [k]: processedValue });
                      }}
                      className={inputClass}
                    />
                  )}
                </React.Fragment>
              );
            })}
          </div>
        </div>
        <div className="edit-document-modal__actions">
          <button 
            type='button' 
            onClick={onCancel} 
            className="edit-document-modal__button edit-document-modal__button--cancel"
          >
            Cancel
          </button>
          <button 
            type='button' 
            disabled={!!saving} 
            onClick={onSave} 
            className="edit-document-modal__button edit-document-modal__button--save"
          >
            {saving? 'Saving...':'Save'}
          </button>
        </div>
      </div>
    </Modal>
  );
};
